import { useEffect } from 'react'
import { toast } from 'sonner'

import {
  buildVersionUrl,
  hasNewVersion,
  parseVersionPayload,
  shouldReloadAfterPreloadError,
} from '@/lib/app-version'
import { env } from '@/lib/env'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const MIN_CHECK_GAP_MS = 30 * 1000
const PRELOAD_RELOAD_KEY = 'motionlab-preload-reload-at'

function readSession(key: string) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeSession(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/**
 * Detects a newer deploy in long-lived tabs (polling, focus, failed chunk loads)
 * and offers a reload. Inert in dev: version.json is only emitted by `vite build`.
 */
export function useAppVersionCheck() {
  useEffect(() => {
    const currentVersion = env.appVersion
    if (!import.meta.env.PROD || !currentVersion) {
      return
    }

    let notifiedVersion: string | null = null
    let inFlight = false
    let lastCheckAt = 0

    const check = async (force = false) => {
      const now = Date.now()
      if (inFlight || (!force && now - lastCheckAt < MIN_CHECK_GAP_MS)) {
        return
      }
      inFlight = true
      lastCheckAt = now
      try {
        const response = await fetch(buildVersionUrl(import.meta.env.BASE_URL, now), {
          cache: 'no-store',
        })
        if (!response.ok) {
          return
        }
        const deployedVersion = parseVersionPayload(await response.json())
        if (!hasNewVersion(currentVersion, deployedVersion) || deployedVersion === notifiedVersion) {
          return
        }
        notifiedVersion = deployedVersion
        toast.info('Neue Version verfügbar', {
          id: 'app-version-update',
          description: 'Lade die Seite neu, um die aktuelle Version zu verwenden.',
          duration: Infinity,
          action: { label: 'Neu laden', onClick: () => window.location.reload() },
        })
      } catch {
        // Offline, blocked or no version.json deployed: ignore silently.
      } finally {
        inFlight = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void check()
      }
    }
    const handleFocus = () => void check()
    const handlePreloadError = (event: Event) => {
      // A lazy chunk of the old build is gone after a deploy: reload once.
      const now = Date.now()
      if (shouldReloadAfterPreloadError(readSession(PRELOAD_RELOAD_KEY), now)
        && writeSession(PRELOAD_RELOAD_KEY, String(now))) {
        event.preventDefault()
        window.location.reload()
        return
      }
      // Already reloaded recently (or no storage for the loop guard): let the error
      // surface and offer a manual reload if a new version is deployed.
      void check(true)
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        void check()
      }
    }, CHECK_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('vite:preloadError', handlePreloadError)
    void check(true)

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('vite:preloadError', handlePreloadError)
    }
  }, [])
}
